// enclosingQueryLevels — the query levels around a subquery body, outermost
// first (the statement, then each enclosing subquery's body), each as a
// QueryStructure of ITS OWN text: the subquery below it is cut down to
// `(SELECT 1)` before the level is extracted.
//
// Why not extract every enclosing body in place: each body contains every
// level below it, so a 30-deep EXISTS chain around a 100 KB IN list re-reads
// those 100 KB thirty times (the drilled picker stalled for ~350 ms per call).
// Here the chain of enclosing parens is found in one pass over the text
// OUTSIDE the body — the opening parens before it, their closes after it —
// and every level is extracted from its own text only, so the whole walk
// reads the statement about once.
//
// Offsets in a level's structure are into `text` (the level's own text); the
// `(` that opens the subquery below sits at `frameOpen` there, and every
// offset before it maps back to the document by adding `textStart`.

import { buildParsedDocumentFromSource, getDocumentStructure } from '../../sql-grammar/document';
import { scanDocument } from '../../sql-grammar/document/scanner';
import type { ParseOptions } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';
import { extractQueryStructure } from '../structure/extractQueryStructure';
import type { ParsedBuffer, QueryStructure } from '../types';
import { skipTrivia } from './dmlRelationLists';
import { frameIsLateralOrApply } from './lateralFrame';

export type EnclosingQueryLevel = {
  /** The level extracted over `text` (offsets are into `text`); null when it could not be extracted. */
  readonly structure: QueryStructure | null;
  /** The level's own text, the subquery below reduced to `(SELECT 1)`. */
  readonly text: string;
  /** Document offset of `text[0]`. */
  readonly textStart: number;
  /** Offset in `text` of the `(` opening the subquery below this level. */
  readonly frameOpen: number;
  /** The subquery below is a LATERAL / CROSS|OUTER APPLY body (its keyword precedes the paren). */
  readonly lateral: boolean;
};

type Paren = { readonly open: number; readonly close: number };

const PLACEHOLDER = 'SELECT 1';
/** A paren whose content opens with one of these is a query level (read from 7 characters, never the whole content). */
const SUBQUERY_HEAD = /^(SELECT|WITH|VALUES|TABLE)(?![\w$])/i;

/**
 * The levels enclosing the subquery body `[bodyStart, bodyEnd)` of statement
 * `statementId`, outermost first (the statement itself is level 0); empty
 * when the statement is unknown or the body sits in no subquery paren.
 */
export function enclosingQueryLevels(
  buffer: ParsedBuffer,
  statementId: string,
  bodyStart: number,
  bodyEnd: number,
): EnclosingQueryLevel[] {
  const { source, options } = buffer.parsedDocument;
  const stmt = buffer.documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt || bodyStart < stmt.range.startOffset || bodyEnd > stmt.range.endOffset) return [];
  const { startOffset: stmtStart, endOffset: stmtEnd } = stmt.range;
  const ctx = skipContextFor(options);
  const parens = enclosingParens(source, stmtStart, stmtEnd, bodyStart, bodyEnd, ctx).filter((p) => {
    const head = skipTrivia(source, p.open + 1, p.close, ctx);
    return SUBQUERY_HEAD.test(source.slice(head, Math.min(head + 7, p.close)));
  });
  const levels: EnclosingQueryLevel[] = [];
  for (let k = 0; k < parens.length; k++) {
    const frame = parens[k]!;
    const start = k === 0 ? stmtStart : parens[k - 1]!.open + 1;
    const end = k === 0 ? stmtEnd : parens[k - 1]!.close;
    const text =
      source.slice(start, frame.open + 1) + PLACEHOLDER + (frame.close < end ? source.slice(frame.close, end) : ')');
    levels.push({
      structure: extractOwnText(text, options),
      text,
      textStart: start,
      frameOpen: frame.open - start,
      lateral: frameIsLateralOrApply(source, frame.open, stmtStart),
    });
  }
  return levels;
}

/**
 * Every paren open at `bodyStart`, outermost first, with its close: the
 * opens come from one pass over `[stmtStart, bodyStart)`, the closes from one
 * pass over `[bodyEnd, stmtEnd)` (an unclosed paren closes at `stmtEnd`).
 */
function enclosingParens(
  source: string,
  stmtStart: number,
  stmtEnd: number,
  bodyStart: number,
  bodyEnd: number,
  ctx: SkipContext,
): Paren[] {
  const opens: number[] = [];
  for (let i = stmtStart; i < bodyStart; ) {
    const skipped = trySkipNonCode(source, i, bodyStart, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    if (source[i] === '(') opens.push(i);
    else if (source[i] === ')') opens.pop();
    i++;
  }
  const closes = opens.map(() => stmtEnd);
  let depth = 0;
  let next = opens.length - 1;
  for (let i = bodyEnd; i < stmtEnd && next >= 0; ) {
    const skipped = trySkipNonCode(source, i, stmtEnd, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    if (source[i] === '(') depth++;
    else if (source[i] === ')') {
      if (depth > 0) depth--;
      else closes[next--] = i;
    }
    i++;
  }
  return opens.map((open, k) => ({ open, close: closes[k]! }));
}

function extractOwnText(text: string, options: ParseOptions): QueryStructure | null {
  try {
    const scan = scanDocument(text, options);
    const parsedDocument = buildParsedDocumentFromSource(text, options, scan);
    const documentStructure = getDocumentStructure(parsedDocument, 'enclosing-level', scan);
    const stmt = documentStructure.statements[0];
    return stmt ? extractQueryStructure({ parsedDocument, documentStructure }, stmt) : null;
  } catch {
    return null;
  }
}

